import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { getUrlUnitOverviewPDFDownload } from 'api/ApiUrls';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import { getClientTimeInISO } from 'library/utils/dateTime/clientTime';
import { vppnReverseProxyType } from 'main/auth/provider';
import {
	PdfPrintFormLayout,
	PdfPrintSwitchConfig,
	PdfPrintTab,
} from 'modules/Shared/PdfPrint/PdfPrintFormLayout';
import { PrintPdfDialogContentProps } from 'modules/Shared/PdfPrint/types';
import { usePdfDownloadSubmit } from 'modules/Shared/PdfPrint/usePdfDownloadSubmit';
import { usePdfPreview } from 'modules/Shared/PdfPrint/usePdfPreview';
import { useStorePdfUserSettings } from 'modules/Shared/PdfPrint/usePdfUserSettings';

import {
	PrintUnitOverviewUserSettings,
	UNIT_OVERVIEW_PDF_DEFAULTS,
	mapFormToUnitOverviewPdfRequest,
} from './types';
import { unitOverviewSettingsCategory } from './unitOverviewSettingsCategory';

type PrintUnitOverviewForm = PrintUnitOverviewUserSettings & {
	additionalText?: string;
};

export const UnitOverviewDialogContent = ({
	id,
	onClose,
	previewDebounce = 1500,
	defaultPdfSettings,
	isDealer,
	setIsDealer,
	isPreview,
	setIsPreview,
}: PrintPdfDialogContentProps<PrintUnitOverviewUserSettings>) => {
	const { t } = useTranslation(['unitOverview']);
	const { api } = useProductCenterApi();
	const { trackFormSubmitSuccess } = useAdobeTracking();
	const updatePdfSettings = useStorePdfUserSettings(
		unitOverviewSettingsCategory
	);

	const defaultValues =
		(isDealer
			? defaultPdfSettings?.forDealer
			: defaultPdfSettings?.forCustomer) ?? UNIT_OVERVIEW_PDF_DEFAULTS;

	const { control, getValues, subscribe, handleSubmit } =
		useForm<PrintUnitOverviewForm>({
			mode: 'onChange',
			defaultValues: { ...defaultValues },
		});

	const buildRequest = useCallback(
		() =>
			mapFormToUnitOverviewPdfRequest(
				id,
				getValues(),
				vppnReverseProxyType(),
				isDealer,
				getValues().additionalText
			),
		[id, getValues, isDealer]
	);

	const getFileName = useCallback(
		() => `Unit preview ${getClientTimeInISO()}.pdf`,
		[]
	);

	const { previewUrl, isLoadingPreview, triggerPreview } = usePdfPreview({
		generate: (signal) =>
			api
				.downloadUnitOverviewPdf(
					buildRequest(),
					{ openFile: false },
					{ format: 'blob', signal }
				)
				.then((response) => response.data),
		getFileName,
		previewDebounce,
		onError: () => setIsPreview(false),
		disabled: !isPreview,
	});

	const { submit, isPending } =
		usePdfDownloadSubmit<PrintUnitOverviewUserSettings>({
			url: getUrlUnitOverviewPDFDownload,
			getFileName,
			buildFormData: buildRequest,
			onSuccess: () => {
				void trackFormSubmitSuccess({
					type: 'unit-overview',
					name: 'Print Unit Overview',
					id: id.toString(),
					destination: 'pdf-download',
					topic: isDealer ? 'dealer' : 'customer',
				});
			},
		});

	const onTabChange = (_: React.SyntheticEvent, newValue: PdfPrintTab) => {
		void updatePdfSettings(getValues(), newValue !== 'dealer'); // save settings for the tab we are leaving
		setIsDealer(newValue === 'dealer');
	};

	useEffect(() => {
		// refresh the preview on any field change (switches, language select,
		// comment box). Driving this off react-hook-form's subscribe API rather
		// than the form's DOM onChange ensures non-native inputs like the MUI
		// language Select are covered too. subscribe is a side-effect listener
		// and does not trigger re-renders.
		if (!isPreview) {
			return undefined;
		}
		return subscribe({
			formState: { values: true },
			callback: () => triggerPreview(),
		});
	}, [subscribe, isPreview, triggerPreview]);

	useEffect(() => {
		// covers turning preview on and switching tabs while preview is active
		if (isPreview) {
			triggerPreview();
		}
	}, [isPreview, isDealer, triggerPreview]);

	const onSubmit = handleSubmit((data) => {
		void updatePdfSettings(data, isDealer);
		void submit(data);
	});

	const onSubmitHandler = (event: React.SubmitEvent<HTMLFormElement>) => {
		void onSubmit(event);
	};

	const switches = useMemo<
		PdfPrintSwitchConfig<PrintUnitOverviewUserSettings>[]
	>(
		() => [
			...(isDealer
				? [
						{
							name: 'printQr' as const,
							label: t('unitOverview:unitOverviewPrintout-qr'),
							helperText: t(
								'unitOverview:unitOverviewPrintout-qr-helper'
							),
						},
					]
				: []),
			{
				name: 'printConfiguration',
				label: t('unitOverview:unitOverviewPrintout-configuration'),
			},
			{
				name: 'printEndCustomer',
				label: t('unitOverview:unitOverviewPrintout-endCustomer'),
			},
			{
				name: 'printCampaigns',
				label: t('unitOverview:unitOverviewPrintout-campaigns'),
			},
			{
				name: 'printCoverage',
				label: t('unitOverview:unitOverviewPrintout-coverages'),
			},
			{
				name: 'printProductInOperation',
				label: t(
					'unitOverview:unitOverviewPrintout-productInOperation'
				),
			},
			{
				name: 'printCommissioning',
				label: t('unitOverview:unitOverviewPrintout-commissioning'),
			},
			{
				name: 'printCommissioningChecksList',
				label: t(
					'unitOverview:unitOverviewPrintout-commissioningChecksList'
				),
			},
			{
				name: 'printSignatures',
				label: t('unitOverview:unitOverviewPrintout-signatures'),
			},
		],
		[isDealer, t]
	);

	return (
		<PdfPrintFormLayout
			control={control}
			onSubmit={onSubmitHandler}
			isPreview={isPreview}
			isPending={isPending}
			description={t('unitOverview:unitOverviewPrintout-info-content')}
			dealerTabLabel={t('unitOverview:unitOverviewPrintout-forDealer')}
			customerTabLabel={t(
				'unitOverview:unitOverviewPrintout-forCustomer'
			)}
			isDealer={isDealer}
			onTabChange={onTabChange}
			additionalInfoTitle={t(
				'unitOverview:unitOverviewPrintout-additionalInformation'
			)}
			commentBoxLabel={t(
				'unitOverview:unitOverviewPrintout-additionalInformation-alt'
			)}
			switchSectionLabel={t(
				'unitOverview:unitOverviewPrintout-switchLabel'
			)}
			switches={switches}
			preview={{
				isLoadingPreview: isLoadingPreview,
				previewUrl: previewUrl,
				objectName: 'UnitOverviewPdf',
				generateButtonLabel: t(
					'unitOverview:unitOverviewPrintout-generate-preview-button'
				),
				onGenerateClick: () => setIsPreview(true),
			}}
			onClose={onClose}
		/>
	);
};
